import { ReactElement, useState, useEffect } from 'react';
import { Document } from '@contentful/rich-text-types';
import RichText from 'components/UI/Richtext';
import CTA from 'components/UI/Cta';
import { IFragmentCta, IFragmentIFrameCta } from 'types/contentful';
import { Asset } from 'contentful';
import Image from 'components/UI/Image';
import { LINK_CONTENT_TYPE } from 'lib/consts';
import IFrameCTA from 'components/UI/IFrameCta';

interface RichContentCardProps {
  titleElement?: ReactElement;
  title: string;
  description?: Document | undefined;
  descriptionAlignment?: 'left' | 'center' | 'right' | undefined;
  backgroundColor?: 'Default - white' | 'Muted' | undefined;
  image?: Asset | undefined;
  aspectRatio?: string;
  descriptionImage?: string;
  ctas?: (IFragmentCta | IFragmentIFrameCta)[] | undefined;
}

export default function RichContentCard({
  titleElement = undefined,
  title = '',
  description = undefined,
  descriptionAlignment = 'center',
  backgroundColor,
  image = undefined,
  ctas = undefined,
}: RichContentCardProps): ReactElement {
  const [background, setBackground] = useState<string>('bg-white');

  useEffect(() => {
    if (backgroundColor && backgroundColor === 'Muted') {
      setBackground('bg-muted');
    } else {
      setBackground('bg-white');
    }
  }, [backgroundColor]);

  const alignmentClass =
    descriptionAlignment === 'left'
      ? 'text-left'
      : descriptionAlignment === 'right'
      ? 'text-right'
      : 'text-center';

  return (
    <div
      className={`ecp-my text-primary-actual ${background} ${
        background === 'bg-white' ? 'pb-10 md:pb-20' : 'py-10 md:py-20'
      }`}
    >
      <div className={`md:w-2/3 mx-auto px-4 md:px-0`}>
        {image && (
          <div className="mb-6 w-1/2 md:w-2/5 mx-auto">
            <Image image={image} query="w=672" classNames="object-cover mx-auto h-auto" />
          </div>
        )}

        {!titleElement ? (
          <h5 className={`text-center text-2xl md:text-4xl leading-tight`}>{title}</h5>
        ) : (
          titleElement
        )}

        <div className="">
          {description && (
            <div className={`mt-4 text-lg`}>
              <RichText
                document={description}
                classNames={`text-lg text-primary-actual ${alignmentClass}`}
              />
            </div>
          )}

          {ctas && ctas.length > 0 && (
            <div className="mt-8 flex flex-col justify-self-center md:flex-row md:space-y-0 space-y-5">
              {ctas.map((cta, index) => (
                <div key={cta.sys?.id ?? index}>
                  {cta.sys.contentType.sys.id === LINK_CONTENT_TYPE.fragmentIFrameCta ? (
                    <IFrameCTA
                      {...cta.fields}
                      backgroundColor="black"
                      classNames="w-full md:w-auto"
                    />
                  ) : (
                    <CTA
                      {...cta.fields}
                      classNames="grow mr-4 lg:mr-6 button-text bg-white w-full md:w-auto"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
